import React, { useEffect, useRef, useState, useCallback } from "react";
import './contoller.css';
import { useMutation, useQuery, useSubscription } from '@apollo/client';
import { COMMAND_SUBSCRIPTION, INITIAL_SUBSCRIPTION, RESET_RENDERER_CONNECTION } from '../../graphql/subscription';
import { GET_COMMANDS, GET_INITIAL_COMMANDS, SYNC_CAPTION_STATE } from '../../graphql/queries';
import { onConnectionStatusChange } from '../../apolloClient';

const baseCaptionsApiUrl = process.env.REACT_APP_RENDERER_API_URL;

// Replay broker state newest-first: the scene's layer manager applies the
// newest command per layer and drops older sibling-caption commands as stale,
// so ordering the batch by recency avoids ever seeking a superseded caption in.
const orderCommandsForReplay = (commands) =>
  [...(commands || [])].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));

const ControllerPage = () => {
  const params = new URLSearchParams(window.location.search);
  const renderer = params.get('renderer');
  const debug = params.get('debug');

  const [isLoaded, setIsLoaded] = useState(false);
  const [scenePath, setScenePath] = useState(null);
  const [captions, setCaptions] = useState(null);
  const [captionData, setCaptionData] = useState({
    renderer,
    caption: { action: 'clear', id: "" },
    updatedBy: 'none'
  });
  const [initialCommadData, setInitialCommadData] = useState({
    eventId: '',
    renderer
  });

  const sceneRef = useRef(null);
  const messageQueue = useRef([]);

  // Safely send data to iframe (with queueing)
  const sendDataToScene = useCallback((message) => {
    if (sceneRef.current && isLoaded) {
      sceneRef.current.contentWindow.postMessage(message, '*');
    } else {
      // Deduplicate by message.type: keep only the latest per type
      const existingIndex = messageQueue.current.findIndex(m => m.type === message.type);
      if (existingIndex !== -1) {
        messageQueue.current[existingIndex] = message; // Replace with the latest
      } else {
        messageQueue.current.push(message); // Add new
      }
    }
  }, [isLoaded]);

  // Queries
  const lastCommand = useQuery(GET_COMMANDS, {
    variables: { renderer },
    fetchPolicy: "cache-first",
    skip: !!captionData.caption.data,
  });

  const initialCommand = useQuery(GET_INITIAL_COMMANDS, {
    variables: { renderer },
    fetchPolicy: "cache-first",
    skip: !!initialCommadData.data,
  });

  // Subscriptions
  const commandSubscription = useSubscription(COMMAND_SUBSCRIPTION, {
    variables: { renderer }
  });

  const initialSubscription = useSubscription(INITIAL_SUBSCRIPTION, {
    variables: { renderer }
  });

  const resetRendererConnection = useSubscription(RESET_RENDERER_CONNECTION, {
    variables: { rendererId: renderer }
  });

  const [syncCaptionState] = useMutation(SYNC_CAPTION_STATE);

  // Refetch GET_COMMANDS when the component is loaded
  useEffect(() => {
    if (isLoaded && lastCommand.refetch) {
      lastCommand.refetch().then(({ data }) => {
        if (data?.getCommands) {
          orderCommandsForReplay(data.getCommands).forEach(commandData => {
            setCaptionData(prev => ({
              ...prev,
              ...commandData,
              updatedBy: 'refetch'
            }));
            sendDataToScene({ data: commandData, type: 'command', updatedBy: 'refetch' });
          });
        }
      }).catch(err => {
        console.error("Refetching GET_COMMANDS failed:", err);
      });
    }
  }, [isLoaded, lastCommand.refetch, sendDataToScene]);

  // On broker connection loss, do nothing: graphql-ws reconnects on its own and
  // re-establishes the subscriptions, so live commands simply resume. Deliberately
  // NO state replay on reconnect — commands missed during the outage are dropped
  // rather than resynced (a late seek mid-show is worse than waiting for the next
  // live command). State replay happens only on scene load (modules-loaded).
  useEffect(() => {
    console.log('[WS] resync on reconnect: OFF (OPUS-0000)');
    let downAt = null;
    return onConnectionStatusChange((status) => {
      if (status === 'disconnected') {
        if (downAt == null) downAt = Date.now();
        console.warn('[WS] Connection to broker lost (disconnected).');
      } else if (status === 'connected' && downAt != null) {
        console.log(`[WS] Connection to broker restored after ${Date.now() - downAt} ms - no state resync (OPUS-0000).`);
        downAt = null;
      }
    });
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      // Scene-initiated state report (e.g. auto-OUT on a take): patch the
      // broker's stored row so operator tooling sees the truth. Not a command —
      // the broker publishes nothing back to renderers for these.
      if (event.data?.type === 'scene-state-sync') {
        const { captionId, action, mode, at } = event.data;
        if (captionId && action) {
          syncCaptionState({ variables: { payload: { renderer, captionId, action, mode, at } } })
            .catch(err => console.error('[SceneSync] Failed to report scene state to broker:', err));
        }
        return;
      }
      if (event.data?.type === 'modules-loaded') {
        console.log('Modules loaded message received from iframe.');
        setIsLoaded(true);

        // Send all queued messages EXCEPT commands
        const remainingQueue = [];

        while (messageQueue.current.length > 0) {
          const msg = messageQueue.current.shift();
          if (msg.type === 'command') {
            // Skip sending, we'll refetch instead
            console.log('⏭️ Skipping old command message, will refetch.');
          } else {
            sceneRef.current?.contentWindow.postMessage(msg, '*');
          }
        }

        // Refetch latest commands
        if (lastCommand.refetch) {
          lastCommand.refetch().then(({ data }) => {
            if (data?.getCommands) {
              orderCommandsForReplay(data.getCommands).forEach(commandData => {
                const msg = {
                  data: commandData,
                  type: 'command',
                  updatedBy: 'refetch'
                };
                sceneRef.current?.contentWindow.postMessage(msg, '*');
              });
            }
          }).catch(err => {
            console.error("Refetching GET_COMMANDS failed:", err);
          });
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [lastCommand, syncCaptionState, renderer]);


  // Fetch captions
  useEffect(() => {
    const fetchCaptions = async () => {
      try {
        const response = await fetch(`${baseCaptionsApiUrl}/v1/renderers/${renderer}/details?attachBothSceneCaption=true`);
        if (!response.ok) throw new Error('Network response was not ok');
        const json = await response.json();
        let formattedData = json;
        if (json.success && json.data) {
          const scene = json.data.allocation_details?.scene || {};
          formattedData = {
            organization: json.data.organization || {},
            scene_path: scene.path || "",
            load_part_path: "",
            captions: scene.captions || []
          };
        }
        setCaptions(formattedData);
      } catch (error) {
        console.error('Error fetching captions:', error);
      }
    };
    fetchCaptions();
  }, [renderer, initialCommadData.data, isLoaded]);

  // Command subscription
  useEffect(() => {
    const data = commandSubscription.data?.subscriptionToCommand;
    if (data?.caption?.data) {
      setCaptionData(prev => ({
        ...prev,
        ...data,
        updatedBy: 'subscription'
      }));
      sendDataToScene({ data, type: 'command', updatedBy: 'subscription' });
    }
  }, [commandSubscription, sendDataToScene]);

  // Last commands (query)
  useEffect(() => {
    if (!isLoaded && lastCommand.data?.getCommands) {
      const commandData = lastCommand.data.getCommands;
      setCaptionData(prev => ({
        ...prev,
        ...commandData[0],
        updatedBy: 'query'
      }));
      commandData.forEach(data => {
        sendDataToScene({ data, type: 'command', updatedBy: 'query' });
      });
    }
  }, [lastCommand, isLoaded, sendDataToScene]);

  // Initial subscription
  useEffect(() => {
    const newInitialData = initialSubscription.data?.subscriptionToInitialCommand;
    if (newInitialData) {
      setInitialCommadData(prev => ({
        ...prev,
        ...newInitialData
      }));

      const newScenePath = newInitialData.scenePath || captions?.scene_path;
      if (newScenePath !== scenePath) {
        setIsLoaded(false);
        setScenePath(newScenePath);
      }

      sendDataToScene({ data: newInitialData, type: 'initial', updatedBy: 'subscription' });
    }
  }, [initialSubscription, scenePath, sendDataToScene, captions]);

  // Initial commands (query)
  useEffect(() => {
    const newInitialData = initialCommand.data?.getInitialCommands;
    if (newInitialData) {
      setInitialCommadData(prev => ({
        ...prev,
        ...newInitialData
      }));

      const newScenePath = newInitialData.scenePath || captions?.scene_path;
      if (newScenePath !== scenePath) {
        setIsLoaded(false);
        setScenePath(newScenePath);
      }

      sendDataToScene({ data: newInitialData, type: 'initial', updatedBy: 'query' });
    }
  }, [initialCommand, scenePath, isLoaded, sendDataToScene, captions]);

  // Reset renderer connection
  useEffect(() => {
    const resetData = resetRendererConnection.data?.resetRendererConnection;
    if (resetData?.reset) {
      console.log(`Renderer ${resetData.rendererId} connection reset.`);

      // Reload iframe
      if (sceneRef.current) {
        sceneRef.current.src = scenePath; // Reload the current scene
      }
      setIsLoaded(false); // Reset loaded state
    }
  }, [resetRendererConnection, scenePath]);

  // Captions to scene
  useEffect(() => {
    if (!captions) return;
    sendDataToScene({ data: captions, type: 'caption', updatedBy: 'query' });
  }, [captions, isLoaded, sendDataToScene]);

  // Flush queue on iframe load
  const handleIframeLoad = () => {
    // Send only 'caption' messages first
    const remainingQueue = [];

    for (const msg of messageQueue.current) {
      if (msg.type === 'caption') {
        sceneRef.current?.contentWindow.postMessage(msg, '*');
      } else {
        remainingQueue.push(msg); // Keep others
      }
    }

    messageQueue.current = remainingQueue;
  };

  return (
    <div
      style={{
        position: 'absolute',
        width: '100vw',
        height: '56.25vw', // TODO: Aspect ratio should be dynamic and coming from broken
        maxWidth: '177.78vh',
        maxHeight: '100vh',
        backgroundImage: debug === 'true' ? `url(/images/bg.png)` : '',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        margin: 'auto',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
      }}
    >
      {scenePath && (
        <iframe
          ref={sceneRef}
          src={scenePath + (params.toString() ? `?${params.toString()}` : '')}
          title="Layer"
          style={{
            width: "100%",
            height: "100%",
            position: 'absolute',
            top: '0',
            left: '0',
            border: 'none'
          }}
          onLoad={handleIframeLoad}
        />
      )}
    </div>
  );
};

export default ControllerPage;
